import { useState } from "react";
import CopyCode from "../CopyCode/CopyCode";
import Icon from "../Icon";

const AccordionComponent = () => {
	const [openAccordions, setOpenAccordions] = useState({
		basic1: true,
		basic2: false,
		basic3: false,
		advanced1: true,
		advanced2: false,
		advanced3: false,
	});

	const toggleAccordion = (id) => {
		setOpenAccordions(prev => ({
			...prev,
			[id]: !prev[id]
		}));
	};

	return (
		<div className="flex flex-col gap-y-10 p-6">
			{/* SECTION 1: BASIC ACCORDIONS */}
			<div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
				<h2 className="text-xl font-bold text-white mb-6">Basic Accordions</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">

					{/* Variation 1: Simple Border */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Simple Border</label>
						<div className="space-y-2">
							<details className="border border-slate-600 rounded cursor-pointer group" open>
								<summary className="p-3 font-semibold text-slate-100 group-open:text-amber-400 transition">
									What is React?
								</summary>
								<p className="px-3 pb-3 text-slate-400">React is a JavaScript library for building user interfaces with reusable components.</p>
							</details>
							<details className="border border-slate-600 rounded cursor-pointer group">
								<summary className="p-3 font-semibold text-slate-100 group-open:text-amber-400 transition">
									Why use React?
								</summary>
								<p className="px-3 pb-3 text-slate-400">React simplifies UI development with a component-based approach and efficient rendering.</p>
							</details>
						</div>
						<CopyCode number_code={1} code={`<details className="border border-slate-600 rounded cursor-pointer group" open>
	  <summary className="p-3 font-semibold text-slate-100 group-open:text-amber-400">
    Question?
  </summary>
  <p className="px-3 pb-3 text-slate-400">Answer here.</p>
</details>`} />
					</div>

					{/* Variation 2: Colored Background */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Colored Background</label>
						<div className="space-y-2">
						<details className="bg-slate-700/50 border border-amber-600/40 rounded cursor-pointer group" open>
							<summary className="p-3 font-semibold text-amber-300 group-open:bg-amber-600/20 transition">
								Getting Started
								</summary>
								<p className="px-3 pb-3 text-slate-300">Install React and build your first component.</p>
							</details>
						<details className="bg-slate-700/50 border border-amber-600/40 rounded cursor-pointer group">
							<summary className="p-3 font-semibold text-amber-300 group-open:bg-amber-600/20 transition">
								Documentation
								</summary>
								<p className="px-3 pb-3 text-slate-300">Comprehensive guides and API reference.</p>
							</details>
						</div>
							<CopyCode number_code={2} code={`<details className="bg-slate-700/50 border border-amber-600/40 rounded cursor-pointer group">
  <summary className="p-3 font-semibold text-amber-300">
    Title
  </summary>
  <p className="px-3 pb-3 text-slate-300">Content</p>
</details>`} />
					</div>

					{/* Variation 3: With Icon Indicator */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Icon Indicator</label>
						<div className="space-y-2">
							<details className="border border-slate-600 rounded cursor-pointer group">
							<summary className="p-3 font-semibold text-slate-100 flex items-center justify-between group-open:text-amber-400" open>
									Core Concepts
									<Icon name="chevronDown" className="w-4 h-4 group-open:rotate-180 transition-transform" />
								</summary>
								<p className="px-3 pb-3 text-slate-400">Learn about components, hooks, and state management.</p>
							</details>
							<details className="border border-slate-600 rounded cursor-pointer group" open>
								<summary className="p-3 font-semibold text-slate-100 flex items-center justify-between group-open:text-amber-400">
									Advanced Topics
									<Icon name="chevronDown" className="w-4 h-4 group-open:rotate-180 transition-transform" />
								</summary>
								<p className="px-3 pb-3 text-slate-400">Explore performance optimization and custom hooks.</p>
							</details>
						</div>
						<CopyCode number_code={3} code={`<details className="border border-slate-600 rounded cursor-pointer group">
  <summary className="p-3 font-semibold flex items-center justify-between">
    Title
	<span className="group-open:rotate-180 transition-transform">icon</span>
  </summary>
  <p className="px-3 pb-3 text-slate-400">Content</p>
</details>`} />
					</div>

					{/* Variation 4: Minimal Style */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Minimal Style</label>
						<div className="space-y-2">
							<details className="py-2 border-b border-slate-600 cursor-pointer group" open>
								<summary className="font-semibold text-slate-100 group-open:text-amber-400 pb-2">
									Hooks Introduction
								</summary>
								<p className="text-sm text-slate-400 pt-2">useState, useEffect, and other fundamental hooks.</p>
							</details>
							<details className="py-2 border-b border-slate-600 cursor-pointer group">
								<summary className="font-semibold text-slate-100 group-open:text-amber-400 pb-2">
									Context API
								</summary>
								<p className="text-sm text-slate-400 pt-2">Manage global state with Context and useContext.</p>
							</details>
						</div>
						<CopyCode number_code={4} code={`<details className="py-2 border-b border-slate-600 cursor-pointer group">
	  <summary className="font-semibold text-slate-100 group-open:text-amber-400 pb-2">
    Title
  </summary>
  <p className="text-sm text-slate-400 pt-2">Content</p>
</details>`} />
					</div>

					{/* Variation 5: Card Style */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Card Style</label>
						<div className="space-y-3">
							<details className="bg-gradient-to-r from-slate-700 to-slate-800 border border-slate-500 rounded-lg p-4 cursor-pointer group" open>
								<summary className="font-semibold text-slate-100 flex items-center gap-2">
									<Icon name="lightbulb" className="w-5 h-5" /> Best Practices
								</summary>
								<p className="text-slate-300 mt-3">Use functional components and hooks for better maintainability.</p>
							</details>
							<details className="bg-gradient-to-r from-slate-700 to-slate-800 border border-slate-500 rounded-lg p-4 cursor-pointer group">
								<summary className="font-semibold text-slate-100 flex items-center gap-2">
									<Icon name="rocket" className="w-5 h-5" /> Performance Tips
								</summary>
								<p className="text-slate-300 mt-3">Memoization, lazy loading, and code splitting improve app performance.</p>
							</details>
						</div>
						<CopyCode number_code={5} code={`<details className="bg-gradient-to-r from-slate-700 to-slate-800 border border-slate-500 rounded-lg p-4 group">
  <summary className="font-semibold text-slate-100 flex items-center gap-2">
	<span>Icon</span> Title
  </summary>
  <p className="text-slate-300 mt-3">Content</p>
</details>`} />
					</div>

					{/* Variation 6: Full Width Solid */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Full Width CTA</label>
						<div className="space-y-2">
								<details className="bg-amber-900/40 border border-amber-600/50 rounded-lg cursor-pointer group">
									<summary className="w-full p-4 font-semibold text-amber-300 group-open:bg-amber-900/60 transition flex justify-between items-center">
									<span>Frequently Asked Questions</span>
									<span className="text-lg">+</span>
								</summary>
								<div className="p-4 border-t border-amber-600/50 text-slate-300">
									<p>Get answers to common questions about React development.</p>
								</div>
							</details>
								<details className="bg-amber-900/40 border border-amber-600/50 rounded-lg cursor-pointer group" open>
									<summary className="w-full p-4 font-semibold text-amber-300 group-open:bg-amber-900/60 transition flex justify-between items-center">
									<span>Support & Resources</span>
									<span className="text-lg">+</span>
								</summary>
								<div className="p-4 border-t border-amber-600/50 text-slate-300">
									<p>Find documentation, community, and official support channels.</p>
								</div>
							</details>
						</div>
						<CopyCode number_code={6} code={`<details className="bg-amber-900/40 border border-amber-600/50 rounded-lg group">
  <summary className="w-full p-4 font-semibold text-amber-300 flex justify-between">
    <span>Title</span>
    <span>+</span>
  </summary>
  <div className="p-4 border-t border-amber-600/50 text-slate-300">
    Content
  </div>
</details>`} />
					</div>

				</div>
			</div>

			{/* SECTION 2: ADVANCED ACCORDIONS */}
			<div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
				<h2 className="text-xl font-bold text-amber-400 mb-6 flex items-center gap-2"><Icon name="sparkles" className="w-5 h-5" /> Advanced Accordions</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">

					{/* Variation 7: Multi-Level */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Multi-Level</label>
						<div className="space-y-2">
							<details className="border border-slate-600 rounded cursor-pointer group">
								<summary className="p-3 font-semibold text-slate-100 flex items-center gap-2">
									<Icon name="folder" className="w-4 h-4" /> Installation
								</summary>
								<div className="pl-6 pb-3 text-slate-400 space-y-2">
									<details className="border border-slate-700 rounded p-2" open>
										<summary className="font-medium text-sm">NPM</summary>
										<p className="text-xs pt-2">npm install react</p>
									</details>
									<details className="border border-slate-700 rounded p-2">
										<summary className="font-medium text-sm">Yarn</summary>
										<p className="text-xs pt-2">yarn add react</p>
									</details>
								</div>
							</details>
						</div>
						<CopyCode number_code={7} code={`<details className="border border-slate-600 rounded group">
  <summary className="p-3 font-semibold flex items-center gap-2">
	<span>Icon</span> Title
  </summary>
  <div className="pl-6 pb-3 space-y-2">
    <details className="border border-slate-700 rounded p-2">
      <summary className="font-medium text-sm">Sub Item</summary>
      <p className="text-xs pt-2">Content</p>
    </details>
  </div>
</details>`} />
					</div>

					{/* Variation 8: With Action Buttons */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">With Actions</label>
						<div className="space-y-2">
							<details className="border border-amber-600/40 bg-amber-900/20 rounded cursor-pointer group" open>
								<summary className="p-3 font-semibold text-amber-300 flex items-center justify-between">
									<span className="flex items-center gap-2"><Icon name="fileText" className="w-4 h-4" /> Tutorial</span>
									<Icon name="star" className="w-4 h-4" />
								</summary>
								<div className="p-3 border-t border-amber-600/40 text-slate-300 space-y-3">
									<p>Complete guide to mastering React development.</p>
									<div className="flex gap-2">
										<button className="px-3 py-1 bg-amber-600 hover:bg-amber-500 rounded text-sm">View</button>
										<button className="px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded text-sm">Bookmark</button>
									</div>
								</div>
							</details>
						</div>
						<CopyCode number_code={8} code={`<details className="border border-amber-600/40 bg-amber-900/20 rounded group">
  <summary className="p-3 font-semibold text-amber-300 flex justify-between">
    <span>Title</span>
	<span>Icon</span>
  </summary>
  <div className="p-3 border-t border-amber-600/40 text-slate-300 space-y-3">
    <p>Content</p>
    <div className="flex gap-2">
      <button className="px-3 py-1 bg-amber-600 rounded text-sm">Action</button>
    </div>
  </div>
</details>`} />
					</div>

					{/* Variation 9: Numbered Steps */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Numbered Steps</label>
						<div className="space-y-2">
							<details className="border border-slate-600 rounded cursor-pointer group">
								<summary className="p-3 font-semibold text-slate-100 flex items-center gap-3">
									<span className="w-6 h-6 rounded-full bg-amber-600 flex items-center justify-center text-xs">1</span>
									Setup Project
								</summary>
								<p className="px-9 pb-3 text-slate-400">Create a new React project using create-react-app.</p>
							</details>
							<details className="border border-slate-600 rounded cursor-pointer group">
								<summary className="p-3 font-semibold text-slate-100 flex items-center gap-3">
									<span className="w-6 h-6 rounded-full bg-amber-600 flex items-center justify-center text-xs">2</span>
									Install Dependencies
								</summary>
								<p className="px-9 pb-3 text-slate-400">Install required packages using npm or yarn.</p>
							</details>
							<details className="border border-slate-600 rounded cursor-pointer group" open>
								<summary className="p-3 font-semibold text-slate-100 flex items-center gap-3">
									<span className="w-6 h-6 rounded-full bg-amber-600 flex items-center justify-center text-xs">3</span>
									Build Components
								</summary>
								<p className="px-9 pb-3 text-slate-400">Create reusable React components for your app.</p>
							</details>
						</div>
						<CopyCode number_code={9} code={`<details className="border border-slate-600 rounded group">
  <summary className="p-3 font-semibold flex items-center gap-3">
    <span className="w-6 h-6 rounded-full bg-amber-600 flex items-center justify-center text-xs">
      1
    </span>
    Step Title
  </summary>
  <p className="px-9 pb-3 text-slate-400">Description</p>
</details>`} />
					</div>

					{/* Variation 10: Code Snippets */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Code Snippets</label>
						<div className="space-y-2 font-mono text-xs">
							<details className="border border-slate-600 rounded cursor-pointer group bg-slate-950" open>
								<summary className="p-3 font-semibold text-slate-100 hover:text-amber-400">
									Hook Example
								</summary>
								<pre className="p-3 text-slate-300 overflow-auto bg-slate-950/80 rounded">
{`const [count, setCount] = useState(0);

return (
  <button onClick={() => setCount(count + 1)}>
    Count: {count}
  </button>
);`}
								</pre>
							</details>
						</div>
						<CopyCode number_code={10} code={`<details className="border border-slate-600 rounded group bg-slate-950">
  <summary className="p-3 font-semibold text-slate-100">Code Title</summary>
  <pre className="p-3 text-slate-300 overflow-auto bg-slate-950/80 rounded">
    {/* Code goes here */}
  </pre>
</details>`} />
					</div>

					{/* Variation 11: Animated Background */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Animated Style</label>
						<div className="space-y-2">
							<details className="border-l-4 border-l-amber-500 border-t border-r border-b border-slate-600 rounded cursor-pointer group overflow-hidden">
								<summary className="p-3 font-semibold text-slate-100 group-open:text-amber-400 group-open:bg-amber-900/20 transition-colors">
									<span className="inline-flex items-center gap-2"><Icon name="sparkles" className="w-4 h-4" /> Animation Techniques</span>
								</summary>
								<p className="px-3 pb-3 text-slate-400 group-open:bg-amber-900/10">Use CSS animations and transitions for smooth effects.</p>
							</details>
							<details className="border-l-4 border-l-amber-500 border-t border-r border-b border-slate-600 rounded cursor-pointer group overflow-hidden">
								<summary className="p-3 font-semibold text-slate-100 group-open:text-amber-400 group-open:bg-amber-900/20 transition-colors">
									<span className="inline-flex items-center gap-2"><Icon name="palette" className="w-4 h-4" /> Styling Strategies</span>
								</summary>
								<p className="px-3 pb-3 text-slate-400 group-open:bg-amber-900/10">Combine Tailwind CSS with React for responsive designs.</p>
							</details>
						</div>
						<CopyCode number_code={11} code={`<details className="border-l-4 border-l-amber-500 border border-slate-600 rounded group">
  <summary className="p-3 font-semibold text-slate-100 group-open:text-amber-400 group-open:bg-amber-900/20">
	Title
  </summary>
  <p className="px-3 pb-3 text-slate-400 group-open:bg-amber-900/10">Content</p>
</details>`} />
					</div>

				</div>
			</div>
		</div>
	);
};

export default AccordionComponent;



